/**
 * Unequal (Futoshiki) Move Execution
 *
 * Handles all player moves and game actions.
 */

import { GameState, DIRECTIONS, CheckResult } from './types';
import {
  checkComplete,
  isValidMove,
  isImmutable,
  checkLatinSquareError,
  checkConstraintError,
} from './validation';

/**
 * Get pencil mark value at position
 */
export function getPencilMark(
  state: GameState,
  x: number,
  y: number,
  n: number
): boolean {
  const { order } = state;
  return state.hints[y * order * order + x * order + n] ?? false;
}

/**
 * Set pencil mark value at position
 */
function setPencilMark(
  state: GameState,
  x: number,
  y: number,
  n: number,
  value: boolean
): void {
  const { order } = state;
  state.hints[y * order * order + x * order + n] = value;
}

/**
 * Clear all pencil marks for a cell
 */
function clearPencilMarks(state: GameState, x: number, y: number): void {
  const { order } = state;
  for (let n = 0; n < order; n++) {
    setPencilMark(state, x, y, n, false);
  }
}

/**
 * Clear all pencil marks for the entire grid
 */
export function clearAllPencilMarks(state: GameState): void {
  state.hints.fill(false);
}

/**
 * Fill a cell with a number
 */
export function fillCell(
  state: GameState,
  x: number,
  y: number,
  value: number
): boolean {
  if (!isValidMove(state, x, y, value)) {
    return false;
  }

  const { order } = state;

  // Set the value
  state.nums[y * order + x] = value;

  // Clear pencil marks for this cell
  clearPencilMarks(state, x, y);

  // Re-validate and update error flags
  checkComplete(state, true);

  // Check completion
  const result = checkComplete(state, false);
  if (result === CheckResult.COMPLETE) {
    state.completed = true;
  }

  return true;
}

/**
 * Clear a cell
 */
export function clearCell(state: GameState, x: number, y: number): boolean {
  return fillCell(state, x, y, 0);
}

/**
 * Toggle a pencil mark
 */
export function togglePencilMark(
  state: GameState,
  x: number,
  y: number,
  n: number
): boolean {
  const { order } = state;

  // Validate coordinates
  if (x < 0 || x >= order || y < 0 || y >= order) {
    return false;
  }

  // Validate value
  if (n < 0 || n >= order) {
    return false;
  }

  // Can't add pencil marks to immutable or filled cells
  if (isImmutable(state, x, y)) {
    return false;
  }

  if (state.nums[y * order + x] !== 0) {
    return false;
  }

  // Toggle the mark
  const current = getPencilMark(state, x, y, n);
  setPencilMark(state, x, y, n, !current);

  return true;
}

/**
 * Auto-fill pencil marks for all empty cells
 */
export function autoFillPencilMarks(state: GameState): void {
  const { order, nums } = state;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      if (nums[y * order + x] !== 0) {
        continue; // Skip filled cells
      }

      // Try each possible value
      for (let n = 1; n <= order; n++) {
        // Temporarily set the value
        state.nums[y * order + x] = n;

        // Check if this value causes immediate conflict
        const latinValid = checkLatinSquareError(state, x, y, false);
        const constraintValid = checkConstraintError(state, x, y, false);

        if (latinValid && constraintValid) {
          setPencilMark(state, x, y, n - 1, true);
        } else {
          setPencilMark(state, x, y, n - 1, false);
        }

        // Reset
        state.nums[y * order + x] = 0;
      }
    }
  }
}

/**
 * Get a hint (reveal one cell)
 */
export function getHint(state: GameState): { x: number; y: number } | null {
  const { order, nums, solution } = state;

  // Find all empty cells
  const emptyCells: { x: number; y: number }[] = [];

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      if (nums[y * order + x] === 0 && !isImmutable(state, x, y)) {
        emptyCells.push({ x, y });
      }
    }
  }

  if (emptyCells.length === 0) {
    return null; // No empty cells
  }

  // Pick random empty cell
  const idx = Math.floor(Math.random() * emptyCells.length);
  const cell = emptyCells[idx];
  if (!cell) return null;
  const { x, y } = cell;

  // Fill with solution
  state.nums[y * order + x] = solution[y * order + x] ?? 0;
  state.cheated = true;

  // Clear pencil marks
  clearPencilMarks(state, x, y);

  // Re-validate
  checkComplete(state, true);

  // Check completion
  const result = checkComplete(state, false);
  if (result === CheckResult.COMPLETE) {
    state.completed = true;
  }

  return { x, y };
}

/**
 * Solve the entire puzzle
 */
export function solvePuzzle(state: GameState): void {
  const { order, solution } = state;

  // Fill all cells with solution
  for (let i = 0; i < order * order; i++) {
    if (!isImmutable(state, i % order, Math.floor(i / order))) {
      state.nums[i] = solution[i] ?? 0;
    }
  }

  state.cheated = true;
  state.completed = true;

  // Clear all pencil marks
  state.hints.fill(false);

  // Clear errors
  checkComplete(state, true);
}

/**
 * Toggle constraint spent flag
 */
export function toggleConstraintSpent(
  state: GameState,
  x: number,
  y: number,
  direction: number
): boolean {
  const { order, flags } = state;

  // Validate coordinates
  if (x < 0 || x >= order || y < 0 || y >= order) {
    return false;
  }

  // Validate direction
  if (direction < 0 || direction > 3) {
    return false;
  }

  const dir = DIRECTIONS[direction];
  if (!dir) return false;

  // Check if constraint exists
  if (!((flags[y * order + x] ?? 0) & dir.flag)) {
    return false;
  }

  // Toggle spent flag
  state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) ^ dir.spentFlag;

  return true;
}

/**
 * Move selection in a direction
 */
export function moveSelection(
  state: GameState,
  dx: number,
  dy: number
): void {
  const { order } = state;

  state.selectedX = Math.max(0, Math.min(order - 1, state.selectedX + dx));
  state.selectedY = Math.max(0, Math.min(order - 1, state.selectedY + dy));
}

/**
 * Set selection position
 */
export function setSelection(
  state: GameState,
  x: number,
  y: number
): void {
  const { order } = state;

  if (x >= 0 && x < order && y >= 0 && y < order) {
    state.selectedX = x;
    state.selectedY = y;
  }
}
